import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Image } from '@/components/ui/Image'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

interface ImageGalleryProps {
  images: string[]
  productName: string
}

export function ImageGallery({ images, productName }: ImageGalleryProps) {
  const [selected, setSelected] = useState(0)
  const [lightbox, setLightbox] = useState(false)

  if (images.length === 0) return null

  const next = () => setSelected((i) => (i + 1) % images.length)
  const prev = () => setSelected((i) => (i - 1 + images.length) % images.length)

  return (
    <>
      <div className="space-y-4">
        <div className="relative aspect-[4/5] overflow-hidden bg-oyster">
          <button
            onClick={() => setLightbox(true)}
            className="h-full w-full"
            aria-label={`View ${productName} images`}
          >
            <Image
              src={images[selected]}
              alt={productName}
              aspectRatio="4/5"
              containerClassName="h-full w-full"
              className="h-full w-full object-cover"
            />
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full bg-paper/80 text-carbon shadow-card transition-colors hover:bg-paper"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full bg-paper/80 text-carbon shadow-card transition-colors hover:bg-paper"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setSelected(i)}
                className={cn(
                  'relative aspect-square w-20 shrink-0 overflow-hidden bg-oyster transition-all',
                  selected === i ? 'ring-2 ring-ink' : 'opacity-70 hover:opacity-100'
                )}
                aria-label={`Select image ${i + 1}`}
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/90 p-4"
            onClick={() => setLightbox(false)}
          >
            <button
              onClick={() => setLightbox(false)}
              className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-paper/20 text-white"
              aria-label="Close lightbox"
            >
              <X className="h-6 w-6" />
            </button>

            <img
              src={images[selected]}
              alt={productName}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    prev()
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-paper/20 text-white"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    next()
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-paper/20 text-white"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
